
import { Reward } from '../types';
import { userRepository } from '../../data/userRepository';
import { logger } from '../../infrastructure/logger';
import { FINANCE, APP_CONFIG } from '../constants';

export interface UserLevel {
    id: number;
    name: string;
    minPoints: number;
    colorFrom: string;
    colorTo: string;
    textColors: { title: string; desc: string };
    benefits: string[];
}

export const LEVELS: UserLevel[] = [
    { 
        id: 1, 
        name: 'Bronce', 
        minPoints: 0, 
        colorFrom: 'from-orange-700', 
        colorTo: 'to-orange-500', 
        textColors: { title: 'text-orange-700 dark:text-orange-500', desc: 'text-orange-800 dark:text-orange-200' },
        benefits: ['Envíos desde $50.000', 'Atención estándar', 'Acceso al Catálogo'] 
    },
    { 
        id: 2, 
        name: 'Plata', 
        minPoints: 1000, 
        colorFrom: 'from-gray-400', 
        colorTo: 'to-gray-600',
        textColors: { title: 'text-gray-500 dark:text-gray-300', desc: 'text-gray-600 dark:text-gray-200' },
        benefits: ['Envíos desde $40.000', 'Cupones mensuales', 'Atención prioritaria'] 
    },
    { 
        id: 3, 
        name: 'Oro', 
        minPoints: 5000, 
        colorFrom: 'from-yellow-400', 
        colorTo: 'to-yellow-600',
        textColors: { title: 'text-yellow-600 dark:text-yellow-400', desc: 'text-yellow-700 dark:text-yellow-200' },
        benefits: ['Envíos GRATIS', 'Descuentos exclusivos', 'Acceso anticipado a Drops'] 
    },
    { 
        id: 4, 
        name: 'Diamante', 
        minPoints: 15000, 
        colorFrom: 'from-cyan-400', 
        colorTo: 'to-blue-600',
        textColors: { title: 'text-cyan-600 dark:text-cyan-400', desc: 'text-cyan-700 dark:text-cyan-200' },
        benefits: ['Envíos GRATIS ilimitados', 'Regalos sorpresa', 'Línea directa VIP'] 
    }
];

export const gamificationService = {
    // --- READ ONLY HELPERS ---
    getCurrentLevel(points: number) {
        return [...LEVELS].reverse().find(l => points >= l.minPoints) || LEVELS[0];
    },

    getNextLevel(currentLevelId: number) {
        return LEVELS.find(l => l.id === currentLevelId + 1) || null;
    },

    getPointsForNextLevel(points: number) {
        const current = this.getCurrentLevel(points);
        const next = this.getNextLevel(current.id);
        if (!next) return 0;
        return Math.max(0, next.minPoints - points);
    },

    getProgressPercent(points: number) {
        const current = this.getCurrentLevel(points);
        const next = this.getNextLevel(current.id);
        if (!next) return 100;
        
        const range = next.minPoints - current.minPoints;
        const progress = points - current.minPoints;
        return Math.min(100, Math.max(0, (progress / range) * 100));
    },
    
    getAllLevels() {
        return LEVELS;
    },

    // --- TRANSACTIONAL METHODS (Server Logic) ---
    
    async redeemReward(userId: string, reward: Reward): Promise<{ success: boolean; message?: string }> {
        try {
            // 1. Check Points (Double Check Server Side)
            const user = await userRepository.getUserById(userId);
            if (!user) return { success: false, message: 'Usuario no encontrado' };
            
            if (user.points < reward.cost) {
                return { success: false, message: 'Puntos insuficientes en servidor' };
            }

            // 2. Atomic Deduct & Add
            // In Firestore this would be a Transaction/Batch
            await userRepository.updatePoints(userId, -reward.cost);
            await userRepository.addReward(userId, reward);

            return { success: true };
        } catch (e) {
            logger.error("Redemption Error", e instanceof Error ? e : new Error(String(e)));
            return { success: false, message: e instanceof Error ? e.message : 'Error al canjear' };
        }
    },

    async joinRaffle(userId: string, raffleId: string): Promise<{ success: boolean; message?: string }> {
        try {
            await userRepository.addRaffleParticipation(userId, raffleId);
            return { success: true };
        } catch (e) {
            return { success: false, message: e instanceof Error ? e.message : 'Error al unirse' };
        }
    }
};
